/**
 * ProductReceiveModal — opens from the Receive button on a product
 * row. Lists eligible Manage Purchases (bare ledger rows where the
 * supplier is linked to this product and the row isn't allocated yet)
 * via supplier + invoice dropdowns. Operator picks one, allocates the
 * full qty across variants, and Accepts.
 *
 * Accept POSTs `/warehouse/product-purchases/allocate` which atomically
 * snapshots the Manage Purchase into a new wpp row + writes
 * per-variant stock movements. All-or-nothing per the contract — Σ
 * inputs (in whole units) must equal the picked invoice's qty.
 *
 * Adding a NEW supplier-invoice happens on Manage > Purchases (not
 * here). When this modal has no eligible options, it tells the
 * operator to go there.
 */

import { useEffect, useMemo, useState } from 'react'
import { Alert } from '../../components/atoms/Alert'
import { Button } from '../../components/atoms/Button'
import { Field } from '../../components/molecules/Field'
import { Modal } from '../../components/atoms/Modal'
import { Select } from '../../components/atoms/Select'
import { Spinner } from '../../components/atoms/Spinner'
import { VariantQtyTable } from '../../components/molecules/VariantQtyTable'
import type {
  ProductAggregateDto,
  ProductReceiveOptionsDto,
  ReceiveLine,
  SupplierWithInvoicesDto,
  UnusedInvoiceDto,
  VariantOnHandRef
} from '../../../viewmodels/useWarehouseProductPurchases'

interface Props {
  readonly open: boolean
  readonly product: ProductAggregateDto | null
  readonly onClose: () => void
  readonly getOptions: (
    productId: string
  ) => Promise<{ ok: true; data: ProductReceiveOptionsDto } | { ok: false; message: string }>
  readonly onAllocate: (
    input: { managePurchaseId: string; productId: string; lines: ReadonlyArray<ReceiveLine> }
  ) => Promise<{ ok: true; id: string } | { ok: false; field: string | null; message: string }>
}

function formatQty(millis: number): string {
  return (millis / 1000).toLocaleString('en-IN', { maximumFractionDigits: 3 })
}

export function ProductReceiveModal({
  open,
  product,
  onClose,
  getOptions,
  onAllocate
}: Props): JSX.Element | null {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const [allSuppliers, setAllSuppliers] = useState<ReadonlyArray<SupplierWithInvoicesDto>>([])
  const [variants, setVariants] = useState<ReadonlyArray<VariantOnHandRef>>([])

  const [supplierId, setSupplierId] = useState<string>('')
  const [managePurchaseId, setManagePurchaseId] = useState<string>('')
  const [qtyByVariant, setQtyByVariant] = useState<Record<string, number>>({})

  useEffect(() => {
    if (!open || product === null) return
    const ac = new AbortController()
    setLoading(true)
    setError(null)
    setAllSuppliers([])
    setVariants([])
    setSupplierId('')
    setManagePurchaseId('')
    setQtyByVariant({})
    ;(async () => {
      const r = await getOptions(product.product_id)
      if (ac.signal.aborted) return
      setLoading(false)
      if (!r.ok) {
        setError(r.message)
        return
      }
      setAllSuppliers(r.data.suppliers)
      setVariants(r.data.variants)
    })().catch(() => { /* aborted */ })
    return () => ac.abort()
  }, [open, product, getOptions])

  // Suppliers with at least one allocatable Manage Purchase.
  const eligibleSuppliers = useMemo(
    () => allSuppliers.filter((s) => s.invoices.length > 0),
    [allSuppliers]
  )
  const supplier = useMemo<SupplierWithInvoicesDto | null>(
    () => eligibleSuppliers.find((s) => s.supplier_id === supplierId) ?? null,
    [eligibleSuppliers, supplierId]
  )
  const invoice = useMemo<UnusedInvoiceDto | null>(
    () => supplier?.invoices.find((i) => i.purchase_id === managePurchaseId) ?? null,
    [supplier, managePurchaseId]
  )

  // The Manage Purchase's remaining capacity in whole units (the repo's
  // listLinked... returns remaining_millis in `purchased_millis`).
  const invoiceRemaining = invoice === null ? 0 : Math.floor(invoice.purchased_millis / 1000)
  const allocated = Object.values(qtyByVariant).reduce((a, n) => a + n, 0)
  const headroom = invoiceRemaining - allocated
  // Partial allocation allowed (2026-06-04): Accept enables on ANY
  // non-zero allocation up to remaining — no need to fill the whole MP.
  const canAccept = invoice !== null && allocated > 0 && headroom >= 0 && !submitting && !loading

  if (product === null) return null
  // View mode: nothing to allocate (no pending invoices). Dropdowns are
  // already hidden by the eligibleSuppliers branches; here we just swap
  // the title + drop the Accept button so the modal reads as read-only.
  const isView = product.pending_invoice_count === 0

  const handleSupplier = (value: string): void => {
    setSupplierId(value)
    setManagePurchaseId('')
    setQtyByVariant({})
  }
  const handleInvoice = (value: string): void => {
    setManagePurchaseId(value)
    setQtyByVariant({})
  }
  const handleQty = (variantId: string, qty: number): void => {
    setQtyByVariant((s) => ({ ...s, [variantId]: qty }))
  }

  const handleAccept = async (): Promise<void> => {
    if (!canAccept || invoice === null) return
    setSubmitting(true)
    setError(null)
    const lines: ReceiveLine[] = Object.entries(qtyByVariant)
      .filter(([, q]) => q > 0)
      .map(([variant_id, quantity]) => ({ variant_id, quantity }))
    const r = await onAllocate({
      managePurchaseId: invoice.purchase_id,
      productId: product.product_id,
      lines
    })
    setSubmitting(false)
    if (!r.ok) {
      setError(r.message)
      return
    }
    onClose()
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      maxWidth="lg"
      title={`${isView ? 'View' : 'Receive'} — ${product.product_name}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            {isView ? 'Close' : 'Cancel'}
          </Button>
          {isView ? null : (
            <Button onClick={() => void handleAccept()} disabled={!canAccept}>
              {submitting ? 'Saving…' : 'Accept'}
            </Button>
          )}
        </>
      }
    >
      <div className="space-y-4">
        {error !== null ? <Alert tone="error">{error}</Alert> : null}

        {loading ? (
          <div className="flex items-center justify-center py-6"><Spinner /></div>
        ) : (
          <>
            {allSuppliers.length === 0 ? (
              <Alert tone="info">
                No supplier is linked to this product on Manage &gt; Product Suppliers. Add a link there first
                to start receiving stock.
              </Alert>
            ) : eligibleSuppliers.length === 0 ? (
              <Alert tone="info">No invoices for linked supplier.</Alert>
            ) : null}

            {eligibleSuppliers.length > 0 ? (
              <div className="grid grid-cols-2 gap-3">
                <Field label="Supplier" error={null}>
                  <Select
                    value={supplierId}
                    onChange={(e) => handleSupplier(e.target.value)}
                    aria-label="Supplier"
                  >
                    <option value="">— Select a supplier —</option>
                    {eligibleSuppliers.map((s) => (
                      <option key={s.supplier_id} value={s.supplier_id}>
                        {s.supplier_name} ({s.invoices.length} unallocated)
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="Invoice" error={null}>
                  <Select
                    value={managePurchaseId}
                    onChange={(e) => handleInvoice(e.target.value)}
                    disabled={supplier === null}
                    aria-label="Invoice"
                  >
                    <option value="">
                      {supplier === null ? 'Pick supplier first' : '— Select an invoice —'}
                    </option>
                    {supplier?.invoices.map((inv) => (
                      <option key={inv.purchase_id} value={inv.purchase_id}>
                        {inv.invoice_no} — {formatQty(inv.purchased_millis)} units
                      </option>
                    ))}
                  </Select>
                </Field>
              </div>
            ) : null}

            {invoice !== null ? (
              <div className="grid grid-cols-3 gap-x-4 gap-y-1 font-body text-body-sm border border-border rounded-md bg-parchment/40 p-3">
                <div><span className="text-text-secondary">Available to allocate:</span> <span className="font-mono text-charcoal">{invoiceRemaining} units</span></div>
                <div><span className="text-text-secondary">Allocated:</span> <span className="font-mono text-charcoal">{allocated} units</span></div>
                <div><span className="text-text-secondary">Headroom:</span> <span className={`font-mono ${headroom < 0 ? 'text-terra' : 'text-charcoal'}`}>{headroom} units</span></div>
              </div>
            ) : null}

            {variants.length === 0 ? (
              <Alert tone="info">This product has no active variants. Add variants on Products first.</Alert>
            ) : (
              <>
                <p className="font-body text-body-sm text-text-secondary">
                  {invoice === null
                    ? <>Variants and current on-hand stock are shown below. Pick a supplier + invoice to start allocating.</>
                    : <>Allocate any non-zero amount up to {invoiceRemaining} units across variants. Partial allocations are allowed — you can come back and allocate more later.</>}
                </p>
                {invoice === null ? (
                  <VariantQtyTable
                    variants={variants}
                    qtyByVariant={qtyByVariant}
                    onQtyChange={handleQty}
                    disabled={true}
                  />
                ) : (
                  <VariantQtyTable
                    variants={variants}
                    qtyByVariant={qtyByVariant}
                    onQtyChange={handleQty}
                    remaining={headroom}
                    disabled={submitting}
                  />
                )}
              </>
            )}
          </>
        )}
      </div>
    </Modal>
  )
}
